import React from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { MDBBtn, MDBFile, MDBInput } from 'mdb-react-ui-kit';
import { baseURL } from '../api/baseURL';
import { getSnapwizMediaOrigin } from '../api/hostport';
import AdminShell from './layout/AdminShell';
import Loader from '../Loader';
import Modal from '../Modal';
import { countPillStyle } from './layout/listUi';

// The 4 sections are fixed taxonomy (matches Customer app + Admin Server) —
// only the subcategory tiles within them are admin-managed.
const SECTION_ORDER = ['Traditional', 'Candid', 'Reel (iPhone)', 'Reel (DSLR)'];

type PortfolioItemRow = { id: string, category: string, mediaType: 'image' | 'video', url: string, title: string, order: number };
type SubcategoryRow = { id: string, section: string, name: string, thumbnailUrl: string, order: number };

/** `/user_media/...` paths (portfolio media lives on Backend's disk, same as
 * profile pics/banners) need the Backend origin prefixed to load in a
 * browser <img>/<video> tag — mirrors how the mobile apps already resolve
 * these paths (see resolveBackendMediaUri equivalents). */
function resolveMediaUrl(url: string): string {
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) return url;
    return `${getSnapwizMediaOrigin()}${url.startsWith('/') ? '' : '/'}${url}`;
}

type State = {
    requested: boolean,
    err: boolean,
    items: PortfolioItemRow[],
    subcategories: SubcategoryRow[],
    selectedCategory: string,
    addModalOpen: boolean,
    addFile: File | null,
    addTitle: string,
    uploading: boolean,
    uploadErr: string,
    deleteConfirmId: string,
    addSubModalOpen: boolean,
    addSubSection: string,
    addSubName: string,
    addSubFile: File | null,
    subUploading: boolean,
    subUploadErr: string,
    deleteSubConfirmId: string,
};

/**
 * Client-reported: "Portfolio images and videos should be manageable from
 * Admin" (add/remove/update the photos/videos inside a subcategory), and
 * separately "give option to add sub category from admin" — both the
 * subcategory tiles themselves (thumbnail + name, grouped under one of the
 * 4 fixed sections) and the media inside each one are fully admin-managed
 * here; nothing is hardcoded in the Customer app anymore.
 */
class PortfolioPage extends React.Component<{ token?: string }, State> {
    state: State = {
        requested: false,
        err: false,
        items: [],
        subcategories: [],
        selectedCategory: '',
        addModalOpen: false,
        addFile: null,
        addTitle: '',
        uploading: false,
        uploadErr: '',
        deleteConfirmId: '',
        addSubModalOpen: false,
        addSubSection: SECTION_ORDER[0],
        addSubName: '',
        addSubFile: null,
        subUploading: false,
        subUploadErr: '',
        deleteSubConfirmId: '',
    };

    componentDidMount(): void {
        if (!this.props.token) return;
        this.fetchItems();
        this.fetchSubcategories();
    }

    fetchItems = () => {
        this.setState({ requested: true, err: false });
        axios.get(`${baseURL}/portfolio`, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ items: response.data.items || [], requested: false });
                } else {
                    this.setState({ err: true, requested: false });
                }
            })
            .catch(() => this.setState({ err: true, requested: false }));
    };

    fetchSubcategories = () => {
        axios.get(`${baseURL}/portfolio-subcategories`, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    const subcategories: SubcategoryRow[] = response.data.subcategories || [];
                    this.setState((prev) => ({
                        subcategories,
                        selectedCategory: prev.selectedCategory && subcategories.some(s => s.name === prev.selectedCategory)
                            ? prev.selectedCategory
                            : (subcategories[0]?.name || ''),
                    }));
                }
            })
            .catch(() => {});
    };

    openAddModal = () => {
        this.setState({ addModalOpen: true, addFile: null, addTitle: '', uploadErr: '' });
    };

    closeAddModal = () => {
        if (this.state.uploading) return;
        this.setState({ addModalOpen: false });
    };

    submitAdd = async () => {
        if (this.state.uploading || !this.props.token) return;
        if (!this.state.addFile) {
            this.setState({ uploadErr: 'Choose a file first.' });
            return;
        }
        this.setState({ uploading: true, uploadErr: '' });
        try {
            const formData = new FormData();
            formData.append('media', this.state.addFile);
            const uploadRes = await axios.post(`${getSnapwizMediaOrigin()}/upload/portfolio-media`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const { url, mediaType } = uploadRes?.data || {};
            if (!url || !mediaType) throw new Error('upload_failed');

            await axios.post(`${baseURL}/add-portfolio-item`, {
                category: this.state.selectedCategory,
                mediaType,
                url,
                title: this.state.addTitle.trim(),
            }, { headers: { Authorization: `Bearer ${this.props.token}` } });

            this.setState({ uploading: false, addModalOpen: false });
            this.fetchItems();
        } catch (e) {
            this.setState({ uploading: false, uploadErr: 'Could not upload — check the file and try again.' });
        }
    };

    deleteItem = (itemId: string) => {
        if (!this.props.token) return;
        axios.post(`${baseURL}/delete-portfolio-item`, { itemId }, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then(() => {
                this.setState({ deleteConfirmId: '' });
                this.fetchItems();
            })
            .catch(() => this.setState({ deleteConfirmId: '', err: true }));
    };

    openAddSubModal = () => {
        this.setState({ addSubModalOpen: true, addSubSection: SECTION_ORDER[0], addSubName: '', addSubFile: null, subUploadErr: '' });
    };

    closeAddSubModal = () => {
        if (this.state.subUploading) return;
        this.setState({ addSubModalOpen: false });
    };

    submitAddSub = async () => {
        if (this.state.subUploading || !this.props.token) return;
        const name = this.state.addSubName.trim();
        if (!name) {
            this.setState({ subUploadErr: 'Give it a name.' });
            return;
        }
        if (!this.state.addSubFile) {
            this.setState({ subUploadErr: 'Choose a thumbnail image.' });
            return;
        }
        this.setState({ subUploading: true, subUploadErr: '' });
        try {
            const formData = new FormData();
            formData.append('media', this.state.addSubFile);
            const uploadRes = await axios.post(`${getSnapwizMediaOrigin()}/upload/portfolio-media`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const { url } = uploadRes?.data || {};
            if (!url) throw new Error('upload_failed');

            const response = await axios.post(`${baseURL}/add-portfolio-subcategory`, {
                section: this.state.addSubSection,
                name,
                thumbnailUrl: url,
            }, { headers: { Authorization: `Bearer ${this.props.token}` } });

            if (response?.data?.message !== 'success') throw new Error(response?.data?.message || 'failed');

            this.setState({ subUploading: false, addSubModalOpen: false, selectedCategory: name });
            this.fetchSubcategories();
        } catch (e: any) {
            const msg = e?.response?.data?.message;
            this.setState({
                subUploading: false,
                subUploadErr: msg === 'name_already_exists' ? 'A subcategory with that name already exists.' : 'Could not save — check the details and try again.',
            });
        }
    };

    deleteSubcategory = (subcategoryId: string) => {
        if (!this.props.token) return;
        axios.post(`${baseURL}/delete-portfolio-subcategory`, { subcategoryId }, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then(() => {
                this.setState({ deleteSubConfirmId: '' });
                this.fetchSubcategories();
                this.fetchItems();
            })
            .catch(() => this.setState({ deleteSubConfirmId: '', err: true }));
    };

    render(): React.ReactNode {
        const { items, subcategories, selectedCategory, requested } = this.state;
        const selectedSub = subcategories.find(s => s.name === selectedCategory) || null;
        const isVideoOnly = !!selectedSub?.section.startsWith('Reel');
        const categoryItems = items.filter(i => i.category === selectedCategory).sort((a, b) => a.order - b.order);
        const categoryCount = (name: string) => items.filter(i => i.category === name).length;
        const subToDelete = subcategories.find(s => s.id === this.state.deleteSubConfirmId);

        const sections = SECTION_ORDER
            .map(section => ({ section, subs: subcategories.filter(s => s.section === section).sort((a, b) => a.order - b.order) }))
            .filter(s => s.subs.length > 0 || true); // always show all 4 sections, even empty, so "Add subcategory" has a home

        return (
            <AdminShell activePage="portfolio" title="Portfolio" subtitle="Manage the categories, photos and videos shown on the Customer app's Portfolio tab">
                <Modal open={this.state.err} hideSec primaryText="Okay" title="Update" onConfirm={() => this.setState({ err: false })} toggleOpen={() => this.setState({ err: false })} content={<h6>Something went wrong, please try again later</h6>} />
                <Modal
                    open={!!this.state.deleteConfirmId}
                    primaryText="Remove"
                    secondaryText="Cancel"
                    title="Remove item"
                    onConfirm={() => this.deleteItem(this.state.deleteConfirmId)}
                    toggleOpen={() => this.setState({ deleteConfirmId: '' })}
                    content={<h6>Remove this photo/video from the portfolio? This cannot be undone.</h6>}
                />
                <Modal
                    open={!!this.state.deleteSubConfirmId}
                    primaryText="Remove"
                    secondaryText="Cancel"
                    title="Remove subcategory"
                    onConfirm={() => this.deleteSubcategory(this.state.deleteSubConfirmId)}
                    toggleOpen={() => this.setState({ deleteSubConfirmId: '' })}
                    content={<h6>Remove "{subToDelete?.name}"? This also removes every photo/video inside it, and the tile disappears from the Customer app. This cannot be undone.</h6>}
                />
                {requested ? <Loader /> : null}

                <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'start' }}>
                    <div className="card">
                        <div className="card-body">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <h4 className="card-title" style={{ marginBottom: 0 }}>Categories</h4>
                                <MDBBtn size="sm" onClick={this.openAddSubModal}>+ Add</MDBBtn>
                            </div>
                            {sections.map(({ section, subs }) => (
                                <div key={section} style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                                        {section}
                                    </div>
                                    {subs.length === 0 ? (
                                        <div style={{ fontSize: 12, opacity: 0.4, padding: '4px 10px' }}>No subcategories yet</div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {subs.map(sub => (
                                                <div
                                                    key={sub.id}
                                                    onClick={() => this.setState({ selectedCategory: sub.name })}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                        padding: '6px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                                                        background: selectedCategory === sub.name ? 'rgba(0,144,231,0.14)' : 'transparent',
                                                        color: selectedCategory === sub.name ? '#0090e7' : 'inherit',
                                                        fontWeight: selectedCategory === sub.name ? 700 : 500,
                                                    }}
                                                >
                                                    <img src={resolveMediaUrl(sub.thumbnailUrl)} alt="" style={{ width: 26, height: 26, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                                                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.name}</span>
                                                    {categoryCount(sub.name) ? <span style={countPillStyle}>{categoryCount(sub.name)}</span> : null}
                                                    <span
                                                        onClick={(e) => { e.stopPropagation(); this.setState({ deleteSubConfirmId: sub.id }); }}
                                                        title="Remove subcategory"
                                                        style={{ opacity: 0.5, padding: '0 2px', flexShrink: 0 }}
                                                    >
                                                        <i className="mdi mdi-close" />
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            {!selectedSub ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
                                    <div className="badge badge-outline-secondary" style={{ fontSize: 14 }}>Add a subcategory to get started</div>
                                </div>
                            ) : (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
                                        <h4 className="card-title" style={{ marginBottom: 0 }}>{selectedCategory}</h4>
                                        <MDBBtn onClick={this.openAddModal}>{isVideoOnly ? 'Add video' : 'Add photo'}</MDBBtn>
                                    </div>
                                    <p style={{ fontSize: 12.5, opacity: 0.6, marginBottom: 16 }}>
                                        {isVideoOnly
                                            ? 'This is a Reel category — the app shows one video per category. If more than one is added, the first (lowest order number) is used.'
                                            : 'Shown as a photo grid when a customer opens this category.'}
                                    </p>
                                    {categoryItems.length === 0 ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
                                            <div className="badge badge-outline-secondary" style={{ fontSize: 14 }}>No items added yet</div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14 }}>
                                            {categoryItems.map(item => (
                                                <div key={item.id} style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, overflow: 'hidden' }}>
                                                    <div style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', background: 'rgba(255,255,255,0.04)' }}>
                                                        {item.mediaType === 'video' ? (
                                                            <video src={resolveMediaUrl(item.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                                                        ) : (
                                                            <img src={resolveMediaUrl(item.url)} alt={item.title || item.category} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        )}
                                                        {item.mediaType === 'video' ? (
                                                            <span style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6 }}>
                                                                VIDEO
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <div style={{ padding: '8px 10px' }}>
                                                        <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.title}>
                                                            {item.title || '(untitled)'}
                                                        </div>
                                                        <MDBBtn color="secondary" size="sm" style={{ marginTop: 6, width: '100%' }} onClick={() => this.setState({ deleteConfirmId: item.id })}>
                                                            Remove
                                                        </MDBBtn>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <Modal
                    open={this.state.addModalOpen}
                    hideSec={this.state.uploading}
                    primaryText={this.state.uploading ? 'Uploading…' : 'Upload'}
                    title={`Add ${isVideoOnly ? 'video' : 'photo'} — ${selectedCategory}`}
                    onConfirm={this.submitAdd}
                    toggleOpen={this.closeAddModal}
                    content={
                        <div>
                            <div style={{ marginBottom: 12 }}>
                                <MDBFile
                                    accept={isVideoOnly ? 'video/mp4,video/quicktime' : 'image/jpeg,image/png,image/webp,image/gif'}
                                    label={`Choose a ${isVideoOnly ? 'video (MP4/MOV)' : 'photo (JPG/PNG/WEBP/GIF)'}`}
                                    onChange={(e: any) => this.setState({ addFile: e.target.files?.[0] || null })}
                                />
                            </div>
                            <MDBInput
                                contrast
                                label="Title (optional)"
                                type="text"
                                value={this.state.addTitle}
                                onChange={(e) => this.setState({ addTitle: e.target.value })}
                            />
                            {this.state.uploadErr ? <p style={{ color: '#ff6b6b', fontSize: 13, marginTop: 10 }}>{this.state.uploadErr}</p> : null}
                        </div>
                    }
                />

                <Modal
                    open={this.state.addSubModalOpen}
                    hideSec={this.state.subUploading}
                    primaryText={this.state.subUploading ? 'Saving…' : 'Add'}
                    title="Add subcategory"
                    onConfirm={this.submitAddSub}
                    toggleOpen={this.closeAddSubModal}
                    content={
                        <div>
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginBottom: 4 }}>Section</label>
                                <select
                                    className="form-control"
                                    value={this.state.addSubSection}
                                    onChange={(e) => this.setState({ addSubSection: e.target.value })}
                                    style={{ background: 'rgba(255,255,255,0.04)', color: 'inherit' }}
                                >
                                    {SECTION_ORDER.map(section => <option key={section} value={section}>{section}</option>)}
                                </select>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                                <MDBInput
                                    contrast
                                    label="Name (e.g. Family Portrait)"
                                    type="text"
                                    value={this.state.addSubName}
                                    onChange={(e) => this.setState({ addSubName: e.target.value })}
                                />
                            </div>
                            <MDBFile
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                label="Thumbnail image shown on the tile"
                                onChange={(e: any) => this.setState({ addSubFile: e.target.files?.[0] || null })}
                            />
                            {this.state.subUploadErr ? <p style={{ color: '#ff6b6b', fontSize: 13, marginTop: 10 }}>{this.state.subUploadErr}</p> : null}
                        </div>
                    }
                />
            </AdminShell>
        );
    }
}

const mapStateToProps = (state: any) => ({
    token: state.auth?.token,
});

export default connect(mapStateToProps)(PortfolioPage);
